/* Pocket Factory first-order offer. It never interrupts: it opens from the offer tab (window.PocketEmailOffer.open()),
   or by itself only for a shopper who showed buying intent and backed out (Oct 21): they tapped a buy, quick-buy,
   checkout or Shop Pay / wallet button (pf_buy_intent), then came back to the store without ordering (back from
   checkout, the tab visible again, or a later page view with their cart still full), or try to leave after tapping buy
   (desktop: pointer out the top of the window; phone: the back button). Scrolling, idling and time on page never open
   it. It never opens for a shopper who has ordered (pf_purchased, localStorage plus a 1-year cookie, set from
   customer.orders_count, an order page, or a cart that was checked out), who already signed up or has a code, and at
   most once per 7 days per browser. Never within 10 s of a purpose, pack or design change or while the configurator or
   the website form has focus. After signup the
   Worker-issued code is shown, remembered in this browser until an order is placed, applied to the cart, and checkout
   goes through /discount/<code> whenever the cart does not confirm it. */
(function (root) {
  'use strict';
  if (typeof document === 'undefined') return;
  const dialog = document.getElementById('PFEmailOffer');
  if (!dialog || dialog.dataset.packTopReady) return;
  dialog.dataset.packTopReady = 'true';
  const form = dialog.querySelector('form');
  const params = new URLSearchParams(root.location.search);
  const role = String(dialog.dataset.themeRole || root.Shopify?.theme?.role || '').toLowerCase();
  const preview = params.has('preview_theme_id') || !!root.Shopify?.designMode || (!!role && role !== 'main');
  const qa = preview && params.get('pf_email_offer_test') === '1';
  const scope = preview ? '.preview.' + (dialog.dataset.themeId || root.Shopify?.theme?.id || params.get('preview_theme_id') || 'editor') : '';
  const shownKey = 'pf.email50.packtop.v3.shown' + scope;
  const claimedKey = 'pf.email50.requested' + scope;
  const pendingKey = 'pf.email50.pending' + scope;
  const codeKey = 'pf.email50.code' + scope;
  const linkedKey = 'pf.email50.codeLinked' + scope;
  const nonceKey = 'pf.email50.nonce';
  const emailKey = 'pf.email50.signupEmail';
  const storage = (kind, key, value) => {
    try {
      if (value === undefined) return root[kind].getItem(key);
      if (value === null) root[kind].removeItem(key);
      else root[kind].setItem(key, String(value));
    } catch (_) { /* Storage denial must not prevent the offer from opening. */ }
    return null;
  };
  const excludedPage = /\/(?:cart|checkouts?|account|orders)(?:\/|$)/.test(root.location.pathname);
  const expiresAt = Date.parse(dialog.dataset.expiresAt);
  const result = form?.querySelector('[data-pf-email-result]')?.dataset.pfEmailResult;

  function capture(name, properties) {
    if (preview) return;
    try {
      root.posthog?.capture(name, Object.assign({ campaign: 'launch-email50-packtop-v3' }, properties || {}));
    } catch (_) { /* Analytics may never block signup or purchase. */ }
  }

  // Oct 21: popup_shown / popup_signup for the intent-trigger rollout. In theme previews analytics stay off (as above)
  // and the events are kept on window.__pfOfferEvents for QA instead.
  function captureOffer(name, properties) {
    if (preview) { try { (root.__pfOfferEvents = root.__pfOfferEvents || []).push([name, properties || {}]); } catch (_) {} return; }
    capture(name, properties);
  }
  const intentKey = 'pf_buy_intent', purchasedKey = 'pf_purchased', lastShownKey = 'pf.email50.lastShown' + scope;
  const weekMs = 7 * 864e5, intentMs = 3 * 864e5;
  const cookie = name => { try { return (document.cookie.match('(?:^|; )' + name + '=([^;]*)') || [])[1] || ''; } catch (_) { return ''; } };
  function purchased() {
    return storage('localStorage', purchasedKey) === '1' || cookie(purchasedKey) === '1';
  }
  function markPurchased(source) {
    if (purchased()) return;
    storage('localStorage', purchasedKey, '1');
    try { document.cookie = purchasedKey + '=1; max-age=31536000; path=/; SameSite=Lax'; } catch (_) {}
    storage('localStorage', intentKey, null);
    capture('pf_email_offer_suppressed_purchased', { source });
  }
  function intent() {
    try {
      const value = JSON.parse(storage('localStorage', intentKey) || 'null');
      if (value && Number.isFinite(value.t) && Date.now() - value.t < intentMs) return value;
    } catch (_) {}
    return null;
  }
  function recordIntent(source) {
    const before = intent();
    const value = { t: Date.now(), src: source, cart: cookie('cart') || before?.cart || '', path: root.location.pathname };
    storage('localStorage', intentKey, JSON.stringify(value));
    return value;
  }
  function shownRecently() {
    const at = Number(storage('localStorage', lastShownKey));
    return Number.isFinite(at) && at > 0 && Date.now() - at < weekMs;
  }
  // Anyone who has ordered never sees the offer again.
  if (dialog.dataset.hasOrdered === 'true') markPurchased('customer_orders');
  if (/\/(?:account\/)?orders(?:\/|$)|\/thank[_-]you(?:\/|$)/.test(root.location.pathname)) markPurchased('order_page');

  /* The revealed code is applied to the cart, shown truthfully in the cart drawer and on /cart, and carried to
     checkout through /discount/<code> unless /cart.js confirms it. */
  const codePattern = /^[A-Z0-9][A-Z0-9_-]{3,63}$/;
  const shopRoot = String(document.body?.dataset.root || root.Shopify?.routes?.root || '/').replace(/\/?$/, '/');
  let cartState = null, syncing = null, syncAgain = false, applyTried = '';
  function savedCode() {
    const code = String(storage('localStorage', codeKey) || '').trim().toUpperCase();
    return codePattern.test(code) ? code : '';
  }
  function rememberCode(value) {
    const code = String(value || '').trim().toUpperCase();
    if (codePattern.test(code)) storage('localStorage', codeKey, code);
  }
  function forgetCode() {
    storage('localStorage', codeKey, null);
    cartState = null;
    renderCartStatus();
  }
  function codeOnCart(cart, code) {
    return (Array.isArray(cart?.discount_codes) ? cart.discount_codes : [])
      .find(entry => String(entry?.code || '').trim().toUpperCase() === code) || null;
  }
  function confirmed(code) {
    return !!code && cartState?.code === code && cartState.applied;
  }
  function renderCartStatus() {
    const code = savedCode();
    const applied = confirmed(code);
    // Oct 23, 2026: the 50% covers packs of up to 3 cards; bigger packs are regular price.
    const text = !code ? '' : applied ? '50% first-order discount applied to packs of up to 3 cards. Bigger packs are regular price.' : 'Your 50% code ' + code + ' goes to checkout with this cart. It covers packs of up to 3 cards; bigger packs are regular price.';
    document.querySelectorAll('#PocketCart .cart-total, .cart-page .cart-total').forEach(total => {
      let status = total.nextElementSibling?.hasAttribute('data-pf-offer-cart-status') ? total.nextElementSibling : null;
      if (!text) { status?.remove(); return; }
      if (!status) {
        status = document.createElement('p');
        status.className = 'pf-offer-cart-status ph-no-capture';
        status.setAttribute('data-pf-offer-cart-status', '');
        status.setAttribute('data-ph-no-capture', '');
        status.setAttribute('role', 'status');
        total.after(status);
      }
      status.dataset.state = applied ? 'applied' : 'pending';
      if (status.textContent === text) return;
      if (applied) { status.textContent = text; return; }
      const slot = document.createElement('span');
      slot.className = 'pf-offer-cart-status__code';
      slot.textContent = code;
      status.replaceChildren('Your 50% code ', slot, ' goes to checkout with this cart. It covers packs of up to 3 cards; bigger packs are regular price.');
    });
  }
  async function cartJson(path, init) {
    const response = await root.fetch(shopRoot + path, Object.assign({ credentials: 'same-origin' }, init, {
      headers: Object.assign({ Accept: 'application/json' }, init?.headers)
    }));
    if (!response.ok) throw new Error('cart ' + response.status);
    return response.json();
  }
  function syncCart() {
    if (syncing) { syncAgain = true; return syncing; }
    syncing = (async () => {
      const code = savedCode();
      renderCartStatus();
      if (!code || typeof root.fetch !== 'function') return;
      let cart = null;
      try {
        cart = await cartJson('cart.js');
        const attempt = code + ':' + (cart?.item_count ?? '');
        if (!codeOnCart(cart, code) && applyTried !== attempt) {
          applyTried = attempt;
          await cartJson('cart/update.js', {
            method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ discount: code })
          });
          cart = await cartJson('cart.js');
        }
      } catch (_) { /* Checkout still carries the code through /discount/<code>. */ }
      if (savedCode() === code) {
        cartState = { code, applied: codeOnCart(cart, code)?.applicable === true };
        linkCode(code);
      }
      renderCartStatus();
    })().catch(() => {}).finally(() => {
      syncing = null;
      if (syncAgain) { syncAgain = false; syncCart(); }
    });
    return syncing;
  }
  function linkCode(code) {
    // Quick-pay buttons start checkout from the product form, not the cart. Shopify's discount link also sets the
    // discount cookie checkout reads, so it is requested once per session in the background.
    if (storage('sessionStorage', linkedKey) === code) return;
    storage('sessionStorage', linkedKey, code);
    root.fetch('/discount/' + encodeURIComponent(code) + '?redirect=' + shopRoot + 'cart.js', { credentials: 'same-origin' }).catch(() => {});
  }
  function checkoutUrl(url) {
    const target = String(url || shopRoot + 'checkout');
    const code = savedCode();
    if (!code || confirmed(code)) return target;
    try {
      const parsed = new URL(target, root.location.href);
      if (parsed.origin !== root.location.origin || !/\/checkouts?(?:\/|$)/.test(parsed.pathname)) return target;
      return '/discount/' + encodeURIComponent(code) + '?redirect=' +
        encodeURIComponent(parsed.pathname + parsed.search + parsed.hash).replace(/%2F/gi, '/');
    } catch (_) { return target; }
  }
  function edited(target) {
    return Array.from(target.elements || []).some(field => {
      if (field.type === 'checkbox' || field.type === 'radio') return field.checked !== field.defaultChecked;
      if (field.tagName === 'SELECT') return Array.from(field.options).some(option => option.selected !== option.defaultSelected);
      return field.tagName !== 'BUTTON' && 'defaultValue' in field && field.value !== field.defaultValue;
    });
  }
  // Cart drawer and /cart checkout buttons post the cart form; send them through the discount link instead.
  document.addEventListener('submit', event => {
    if (event.defaultPrevented || event.submitter?.name !== 'checkout') return;
    const code = savedCode();
    const target = checkoutUrl(shopRoot + 'checkout');
    if (!code || !target.startsWith('/discount/')) return;
    try {
      if (edited(event.target)) {
        // Edited quantities on /cart must still reach Shopify, so this form posts and carries the code itself.
        let field = event.target.querySelector('input[type="hidden"][name="discount"]');
        if (!field) {
          field = document.createElement('input');
          field.type = 'hidden';
          field.name = 'discount';
          event.target.appendChild(field);
        }
        field.value = code;
        return;
      }
      event.preventDefault();
      document.dispatchEvent(new root.CustomEvent('pocket:offer-checkout', { detail: { url: target } }));
      root.location.assign(target);
    } catch (_) { /* Never hold up checkout. */ }
  });
  const drawer = document.getElementById('PocketCart');
  if (drawer && typeof MutationObserver === 'function') {
    let wait = 0;
    const observer = new MutationObserver(() => {
      if (!drawer.open || !savedCode()) return;
      root.clearTimeout(wait);
      wait = root.setTimeout(syncCart, 150);
    });
    observer.observe(drawer, { attributes: true, attributeFilter: ['open'] });
    const items = drawer.querySelector('[data-cart-items]');
    if (items) observer.observe(items, { childList: true });
  }
  root.addEventListener('pageshow', event => { if (event.persisted && savedCode()) syncCart(); });
  root.PocketOffer = { checkoutUrl, code: () => savedCode() || null };
  if (dialog.dataset.hasOrdered === 'true') forgetCode();
  // A saved code is never re-checked in the background: the signup endpoint is only called by the shopper's own submit.
  else if (savedCode() && result !== 'success') syncCart();

  if (!form || typeof dialog.showModal !== 'function') return;
  let shown = storage('sessionStorage', shownKey) === '1';
  let claimed = storage('localStorage', claimedKey) === '1';
  let reached = false, checkout = false, frame = 0, paymentUntil = 0;
  let previousFocus = null, responsePending = false;
  const startedAt = Date.now(), quietMs = 10000;
  const composer = '[data-pocket-product] form, [data-rv-url-form]';
  let changedAt = 0, activeAt = startedAt, previewAt = 0;

  function visible(element) {
    return !!element && !element.closest('[hidden],[inert],[aria-hidden="true"]') &&
      element.getClientRects().length > 0 && root.getComputedStyle(element).visibility !== 'hidden';
  }
  function temporarilyBlocked() {
    if (document.visibilityState !== 'visible' || checkout || Date.now() < paymentUntil) return true;
    const active = document.activeElement;
    if (active && !dialog.contains(active) && visible(active) &&
      active.matches('input,textarea,select,[contenteditable="true"]')) return true;
    return Array.from(document.querySelectorAll('dialog[open],[aria-modal="true"]')).some(
      element => element !== dialog && visible(element)
    ) || [document.documentElement, document.body].some(node => node?.classList.contains('pf-keyboard-open'));
  }
  // Automatic openings wait 10 s after any configuration change and never cover a focused configurator.
  function interrupting() {
    return Date.now() - changedAt < quietMs || !!document.activeElement?.closest?.(composer);
  }
  function eligible() {
    return !excludedPage && (!Number.isFinite(expiresAt) || Date.now() < expiresAt) &&
      !shown && !claimed && dialog.dataset.subscribed !== 'true' && dialog.dataset.hasOrdered !== 'true' &&
      !purchased() && !savedCode() && !shownRecently();
  }
  // Shopify's bot protection (hCaptcha) owns the signup post: nothing here cancels a first submit or posts the form
  // itself. Shopify returns to /?customer_posted=true#PFEmailOfferForm, which drops the address's ?card=, so the card's
  // slug is kept for that page (pocket-card-questions.liquid puts ?card= back before the card loads). It is kept when the
  // dialog opens and again on submit, in case another submit listener stops the event before this one runs.
  function rememberCard() {
    try {
      const card = new URLSearchParams(root.location.search).get('card');
      if (card && /^[a-z0-9][a-z0-9-]{2,63}$/.test(card)) storage('sessionStorage', 'pf.email50.returnCard', card);
    } catch (_) {}
  }
  function show(reason, force) {
    if (dialog.open || temporarilyBlocked() || (!force && !eligible())) return false;
    previousFocus = document.activeElement;
    try { dialog.showModal(); } catch (_) { return false; }
    if (!dialog.open) return false;
    shown = true;
    rememberCard();
    prepareSignup(false);
    storage('sessionStorage', shownKey, '1');
    storage('localStorage', lastShownKey, String(Date.now()));
    if (!force) storage('localStorage', intentKey, null); // one automatic opening per buying attempt
    capture('pf_email_offer_shown', { trigger: reason });
    captureOffer('popup_shown', { trigger: reason });
    dialog.querySelector('[data-offer-close]')?.focus({ preventScroll: true });
    return true;
  }
  // It opens by itself only where cards are made (the product section), never over a page being read like
  // /pages/docs; asked for (the offer tab), it opens anywhere it is offered.
  const makesCards = () => !!document.querySelector('[data-pocket-product]');
  function autoShow(reason) {
    return !interrupting() && show(reason);
  }
  function check() {
    frame = 0;
    if (dialog.open) {
      if (challengeShowing()) letChallengeThrough();
      return;
    }
    if (excludedPage) return;
    if (responsePending) {
      if (show('signup_response', true)) responsePending = false;
      return;
    }
    // Exposure only: reaching the pack no longer opens the offer, because that interrupted configuring.
    if (reached) return;
    const pack = document.querySelector('[data-pocket-product] form.product-form .quantity-section');
    if (visible(pack) && !pack.closest('dialog')) {
      const rect = pack.getBoundingClientRect();
      const viewportTop = root.visualViewport?.offsetTop || 0;
      if (rect.width > 0 && rect.height > 0 && rect.top <= viewportTop + 1 && root.scrollY > 0) {
        reached = true;
        capture('pf_email_offer_trigger_reached');
      }
    }
  }
  function schedule() {
    if (!frame) frame = root.requestAnimationFrame(check);
  }
  // Oct 21: the 20 s preview-idle opening is gone. A shown preview is still noted, but never opens the offer.
  function previewShown() {
    if (!makesCards()) return;
    previewAt = Date.now();
  }
  function close(reason) {
    if (!dialog.open) return;
    dialog.close();
    release();
    capture('pf_email_offer_dismissed', { method: reason });
    if (previousFocus?.isConnected && visible(previousFocus) &&
      !previousFocus.matches('input,textarea,select')) previousFocus.focus({ preventScroll: true });
  }
  // Shopify's captcha holds the signup while it gets its token, and when it asks for a visible challenge it draws it at
  // the end of the page, outside this dialog. A modal dialog covers the rest of the page and makes it inert, so that
  // challenge could not be clicked. Once a submit is held, or a challenge shows, the dialog stays open in the same place
  // over the same dimmed page but not modal, until the post goes ahead (the page leaves) or the shopper closes it.
  let held = false, scrim = null;
  const challengeFrame = /captcha.*(?:[#&]frame=challenge\b|\/bframe\b)/i; // hCaptcha's challenge, reCAPTCHA's
  function challengeShowing() {
    return !held && Array.from(document.querySelectorAll('iframe')).some(node => {
      if (dialog.contains(node) || !challengeFrame.test(node.src || '') || !visible(node)) return false;
      const r = node.getBoundingClientRect(); // a challenge waiting off-screen is not showing
      return r.bottom > 0 && r.right > 0 && r.top < root.innerHeight && r.left < root.innerWidth;
    });
  }
  function letChallengeThrough() {
    if (!dialog.open || held) return;
    const focused = dialog.contains(document.activeElement) ? document.activeElement : null;
    if (!scrim) {
      scrim = document.createElement('div');
      scrim.className = 'pf-email-offer__scrim';
      scrim.setAttribute('aria-hidden', 'true');
      scrim.addEventListener('click', () => close('backdrop'));
      dialog.before(scrim);
    }
    held = true;
    scrim.hidden = false;
    dialog.setAttribute('data-offer-held', '');
    dialog.close();
    dialog.show();
    focused?.focus({ preventScroll: true });
  }
  function release() {
    if (!held) return;
    held = false;
    dialog.removeAttribute('data-offer-held');
    scrim.hidden = true;
  }
  function newNonce() {
    const bytes = new Uint8Array(16);
    if (!root.crypto?.getRandomValues) return '';
    root.crypto.getRandomValues(bytes);
    return Array.from(bytes, (byte) => byte.toString(16).padStart(2, '0')).join('');
  }
  function setStatus(box, text) {
    const status = box?.querySelector('[data-launch-status]');
    if (status) status.textContent = text;
  }
  async function copyCode(box, button) {
    const code = box.querySelector('[data-launch-code]')?.textContent?.trim() || '';
    if (!code) return;
    try {
      await navigator.clipboard.writeText(code);
    } catch (_) {
      const area = document.createElement('textarea');
      area.value = code;
      area.setAttribute('readonly', '');
      area.className = 'ph-no-capture';
      area.setAttribute('data-ph-no-capture', '');
      box.appendChild(area);
      area.select();
      document.execCommand('copy');
      area.remove();
    }
    button.textContent = 'COPIED';
  }
  function showCode(box, code) {
    const slot = box.querySelector('[data-launch-code]');
    const button = box.querySelector('[data-launch-copy]');
    if (!slot || !button) return;
    slot.textContent = code;
    slot.hidden = false;
    button.hidden = false;
    box.dataset.launchReveal = 'code';
    setStatus(box, 'We’ll add it at checkout on this device, so there’s nothing to paste. It can’t be combined with FACTORY.');
    if (!button.dataset.copyReady) {
      button.dataset.copyReady = 'true';
      button.addEventListener('click', () => { copyCode(box, button); });
    }
  }
  function showRetry(box, message) {
    setStatus(box, message);
    const retry = box.querySelector('[data-launch-retry]');
    if (!retry) return;
    retry.hidden = false;
    if (!retry.dataset.bound) {
      retry.dataset.bound = 'true';
      retry.addEventListener('click', () => {
        box.dataset.launchRequested = '';
        retry.hidden = true;
        setStatus(box, 'Getting your code…');
        revealSignupCode();
      });
    }
  }
  async function revealSignupCode() {
    const box = form.querySelector('[data-pf-email-result="success"]');
    if (!box || box.dataset.launchRequested === 'true') return;
    box.dataset.launchRequested = 'true';
    const email = (box.dataset.signupEmail || storage('sessionStorage', emailKey) || storage('localStorage', emailKey) || '').trim().toLowerCase();
    const nonce = storage('sessionStorage', nonceKey) || storage('localStorage', nonceKey) || '';
    // The host comes from pocket-core.js (production, or a local/staging Worker on preview hosts only).
    const endpoint = dialog.dataset.issuePath ? (root.PocketCore?.workerBase?.() || '') + dialog.dataset.issuePath : (dialog.dataset.issueUrl || '');
    if (!email || !endpoint) {
      showRetry(box, 'We couldn’t show your code yet. Tap Show my code again.');
      return;
    }
    setStatus(box, 'Getting your code…');
    const maxAttempts = 20;
    for (let attempt = 0; attempt < maxAttempts; attempt += 1) {
      try {
        const body = { email, marketingConsent: true };
        if (/^[a-f0-9]{32}$/.test(nonce)) body.nonce = nonce;
        const response = await fetch(endpoint, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify(body),
        });
        const json = await response.json();
        if (json.status === 'pending' || json.status === 'temporary_error') {
          await new Promise((resolve) => root.setTimeout(resolve, Math.min(1500, 350 * (attempt + 1))));
          continue;
        }
        if ((json.status === 'issued' || json.status === 'already_issued') && typeof json.code === 'string' && json.code) {
          const retry = box.querySelector('[data-launch-retry]');
          if (retry) retry.hidden = true;
          showCode(box, json.code);
          capture('pf_email_signup_code_shown');
          rememberCode(json.code);
          syncCart();
          return;
        }
        if (json.status === 'campaign_closed') {
          setStatus(box, 'This offer isn’t available right now. No code was issued.');
          return;
        }
        if (json.status === 'ineligible') forgetCode();
        setStatus(box, 'This email can’t get a first-order code. Questions? Email bryton@getpocketfactory.com.');
        return;
      } catch (_) {
        await new Promise((resolve) => root.setTimeout(resolve, 500));
      }
    }
    showRetry(box, 'We couldn’t show your code yet. Your email is saved. Tap Show my code again.');
  }
  dialog.querySelectorAll('[data-offer-close]').forEach(button => button.addEventListener('click', () => close('button')));
  dialog.addEventListener('cancel', event => { event.preventDefault(); close('escape'); });
  // Not modal while held: Escape is not a close request then, so it is handled here. Any other close drops the dim.
  dialog.addEventListener('keydown', event => {
    if (held && event.key === 'Escape' && !event.defaultPrevented) { event.preventDefault(); close('escape'); }
  });
  dialog.addEventListener('close', () => { if (!dialog.open) release(); });
  dialog.addEventListener('click', event => {
    if (event.target !== dialog) return;
    const r = dialog.getBoundingClientRect();
    if (event.clientX < r.left || event.clientX > r.right || event.clientY < r.top || event.clientY > r.bottom) close('backdrop');
  });
  root.addEventListener('scroll', schedule, { passive: true });
  root.addEventListener('resize', schedule, { passive: true });
  root.visualViewport?.addEventListener('scroll', schedule, { passive: true });
  root.visualViewport?.addEventListener('resize', schedule, { passive: true });
  document.addEventListener('focusout', schedule, true);
  document.addEventListener('close', schedule, true);
  document.addEventListener('visibilitychange', schedule);
  document.addEventListener('shopify:section:load', schedule);
  new MutationObserver(schedule).observe(document.body, {
    subtree: true, childList: true, attributes: true,
    attributeFilter: ['open', 'hidden', 'inert', 'aria-hidden', 'aria-modal', 'class', 'style']
  });
  if (typeof ResizeObserver === 'function') new ResizeObserver(schedule).observe(document.body);
  // Purpose, pack and design changes (configurator form, homepage website form, card editor) start the quiet period.
  const active = () => { activeAt = Date.now(); };
  const changed = event => {
    active();
    if (!event || event.target?.closest?.(composer)) changedAt = activeAt;
  };
  document.addEventListener('input', changed, true);
  document.addEventListener('change', changed, true);
  document.addEventListener('pocket:card-config', () => changed());
  ['pointerdown', 'keydown', 'wheel', 'touchstart', 'visibilitychange'].forEach(type => document.addEventListener(type, active, { capture: true, passive: true }));
  root.addEventListener('scroll', active, { passive: true });
  // A shown preview (brand applied, share link hydrated, or a saved brand on load) arms the 20 s idle opening.
  document.addEventListener('pocket:brand-path', () => { changed(); previewShown(); });
  document.addEventListener('pocket:preview-hydrated', previewShown);
  const personalized = () => document.documentElement.hasAttribute('data-pf-personalized');
  new MutationObserver(() => { if (personalized()) previewShown(); })
    .observe(document.documentElement, { attributes: true, attributeFilter: ['data-pf-personalized'] });
  if (personalized()) previewShown();
  // A design going into the cart keeps the offer away, as a checkout does; only a real checkout start
  // (pocket-analytics.js) is counted as pf_email_offer_checkout_started, never a cart update.
  let checkoutCounted = false;
  const stopForCheckout = started => {
    if (started && !checkoutCounted && claimed) { checkoutCounted = true; capture('pf_email_offer_checkout_started'); }
    checkout = true;
    if (dialog.open) close('checkout');
  };
  document.addEventListener('pocket:checkout-started', () => stopForCheckout(true));
  document.addEventListener('pocket:cart-updated', () => stopForCheckout(false));
  document.addEventListener('pocket:cart-error', () => { checkout = false; checkoutCounted = false; paymentUntil = 0; schedule(); });
  root.addEventListener('pageshow', event => {
    if (event.persisted) { checkout = false; checkoutCounted = false; paymentUntil = 0; }
    schedule();
  });
  document.addEventListener('click', event => {
    const target = event.target;
    const path = typeof event.composedPath === 'function' ? event.composedPath() : [target];
    const wallet = path.some(node => node?.matches?.('.shopify-payment-button,shopify-accelerated-checkout,shopify-accelerated-checkout-cart'));
    const checkoutLink = target?.closest?.('a[href*="/checkout"],button[name="checkout"]');
    if (wallet || checkoutLink) {
      paymentUntil = Date.now() + 10000;
      root.setTimeout(schedule, 10050);
    }
  }, true);
  // What the signup post needs from this script: the shopper's email kept for the code, and a one-time nonce in the
  // customer tags. It is written when the dialog opens and as the email is typed, and again on submit, so the post
  // carries them even when another submit listener (Shopify's captcha) holds or stops the submit event first.
  function prepareSignup(fresh) {
    const emailInput = form.querySelector('input[name="contact[email]"]');
    const emailValue = String(emailInput?.value || '').trim().toLowerCase();
    if (emailValue) storage('sessionStorage', emailKey, emailValue);
    const tags = form.querySelector('input[name="contact[tags]"]');
    const posted = String(tags?.value || '').split(',').map((tag) => tag.trim()).find((tag) => tag.startsWith('pf-email50-n'));
    const nonce = (!fresh && posted && posted.slice('pf-email50-n'.length)) || newNonce();
    if (nonce) {
      storage('sessionStorage', nonceKey, nonce);
      storage('localStorage', nonceKey, nonce);
      if (tags) {
        const kept = String(tags.value || '').split(',').map((tag) => tag.trim()).filter((tag) => tag && !tag.startsWith('pf-email50-n'));
        kept.push('pf-email50-n' + nonce);
        tags.value = kept.join(',');
      }
    }
    return emailValue;
  }
  form.querySelector('input[name="contact[email]"]')?.addEventListener('input', () => prepareSignup(false));
  // One real submit posts once and counts once: a double tap, click plus touchend, or a repeated submit event while
  // the first post is on its way is dropped. A submit another script cancelled (bot check) does not block the next.
  // The listener sits on the window in the capture phase, so it runs before any page-level submit hook; it never
  // cancels a first submit and never posts the form itself, so Shopify's captcha keeps the browser's native post.
  const submitButton = form.querySelector('[type="submit"]');
  let posting = null, postedAt = 0, counted = '';
  root.addEventListener('submit', event => {
    if (event.target !== form) return;
    rememberCard();
    if (posting && !posting.defaultPrevented && Date.now() - postedAt < 10000) {
      event.preventDefault();
      return;
    }
    if (!form.checkValidity()) return;
    const emailValue = prepareSignup(true);
    if (emailValue) storage('localStorage', emailKey, emailValue);
    storage('sessionStorage', pendingKey, '1');
    posting = event;
    postedAt = Date.now();
    if (counted !== (emailValue || '-')) {
      counted = emailValue || '-';
      capture('pf_email_signup_submitted');
    }
    root.setTimeout(() => {
      // Held by another submit listener (Shopify's captcha while it gets its token): its challenge must be clickable.
      if (event.defaultPrevented) { letChallengeThrough(); return; }
      if (!submitButton) return;
      submitButton.setAttribute('aria-busy', 'true');
      root.setTimeout(() => submitButton.removeAttribute('aria-busy'), 10000);
    }, 0);
  }, true);
  root.addEventListener('pageshow', event => {
    if (!event.persisted) return;
    posting = null;
    counted = '';
    submitButton?.removeAttribute('aria-busy');
  });
  if (result && (storage('sessionStorage', pendingKey) === '1' || root.location.hash === '#PFEmailOfferForm')) {
    responsePending = true;
    if (result === 'success') {
      claimed = true;
      storage('localStorage', claimedKey, '1');
      storage('sessionStorage', pendingKey, '');
      capture('pf_email_signup_form_success');
      captureOffer('popup_signup', { stage: 'form_success' });
      revealSignupCode();
      // Back from checkout or a reload must not open the signup again: drop Shopify's posted marker from this entry.
      try {
        const url = new URL(root.location.href);
        url.searchParams.delete('customer_posted');
        root.history.replaceState(root.history.state, '', url.pathname + url.search);
      } catch (_) {}
    }
  }
  const qaButton = document.getElementById('PFEmailOfferQA');
  if (qa && qaButton) {
    qaButton.hidden = false;
    qaButton.addEventListener('click', () => show('preview_test', true));
  }
  /* Oct 21: buying intent, then backing out. Only a tap on a real buy control (not "Preview my cards") or Shopify's
     confirmed cart add arms the offer; it opens when the shopper comes back without ordering or tries to leave. */
  const fineHover = () => !!root.matchMedia?.('(hover: hover) and (pointer: fine) and (min-width: 900px)').matches;
  let trapArmed = false, trapTimer = 0;
  function armBackTrap() {
    // Phones: after a buy tap that did not leave the page (cart drawer, wallet sheet closed), the next Back opens the
    // offer once instead of leaving. One extra history entry at the same address, only while the offer can open.
    if (trapArmed || fineHover() || !eligible() || excludedPage) return;
    try { root.history.pushState(Object.assign({}, root.history.state, { pfOfferTrap: 1 }), '', root.location.href); trapArmed = true; } catch (_) {}
  }
  root.addEventListener('popstate', event => {
    if (!trapArmed || event.state?.pfOfferTrap) return;
    trapArmed = false;
    // Could not open right now (a wallet sheet in its 10 s, a just-made change): finish the Back they asked for.
    if (!(intent() && !purchased() && autoShow('back_attempt'))) { try { root.history.back(); } catch (_) {} }
  });
  function noteIntent(source) {
    if (excludedPage || purchased()) return;
    recordIntent(source);
    root.clearTimeout(trapTimer);
    trapTimer = root.setTimeout(() => { if (document.visibilityState === 'visible') armBackTrap(); }, 2500);
  }
  const buyControl = '.shopify-payment-button,shopify-accelerated-checkout,shopify-accelerated-checkout-cart,' +
    '[data-pay-area],button[name="add"],button[name="checkout"],a[href*="/checkout"],[data-checkout],[data-purchase],[data-pfx-buy],[data-add],' +
    '[data-quick-buy],[data-pf-quick-buy]';
  document.addEventListener('click', event => {
    const path = typeof event.composedPath === 'function' ? event.composedPath() : [event.target];
    const node = path.find(el => el?.matches?.(buyControl));
    if (!node || dialog.contains(node)) return;
    // The buy bar's [data-purchase] / [data-pfx-buy] read "Preview my cards" until a preview exists; that is designing.
    if (node.matches('[data-purchase],[data-pfx-buy]') && /preview/i.test(node.textContent || '')) return;
    const kind = node.matches('.shopify-payment-button,shopify-accelerated-checkout,shopify-accelerated-checkout-cart,[data-pay-area]') ? 'wallet'
      : node.matches('button[name="checkout"],a[href*="/checkout"],[data-checkout]') ? 'checkout' : node.matches('button[name="add"],[data-add]') ? 'add' : 'buy';
    noteIntent(kind);
  }, true);
  document.addEventListener('pocket:cart-updated', () => noteIntent('cart_add'));
  document.addEventListener('pocket:checkout-started', () => noteIntent('checkout_started'));
  // Back without ordering? A cart that still has items is a clear yes. A checked-out cart (Shopify gives the browser a
  // new cart) is an order. An empty cart after a wallet tap is unclear: only a return to this same page (bfcache, tab)
  // counts then, never a fresh page view, so a buyer coming back from the thank-you page is not asked.
  async function backedOut(sameView) {
    const seen = intent();
    if (!seen || purchased()) return false;
    let count = null;
    try { count = (await cartJson('cart.js'))?.item_count; } catch (_) {}
    const now = cookie('cart');
    if (count > 0) return true;
    if (seen.cart && now !== seen.cart && count === 0) { markPurchased('cart_checked_out'); return false; }
    return sameView;
  }
  let returnTry = 0, returning = false;
  async function returned(reason, sameView) {
    if (returning || excludedPage || dialog.open || !eligible() || !intent()) return;
    if (Date.now() - intent().t < 1500) return; // the tap itself, not a return
    returning = true;
    let back = false;
    try { back = await backedOut(sameView); } finally { returning = false; }
    if (!back) return;
    if (autoShow(reason)) return;
    // Blocked for now (keyboard, another dialog, a just-made change): try again shortly, for up to ~30 s.
    if (++returnTry < 30) root.setTimeout(() => returned(reason, sameView), 1000);
  }
  root.addEventListener('pageshow', event => { if (event.persisted) { returnTry = 0; returned('back_from_checkout', true); } });
  let hiddenAt = 0;
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') { hiddenAt = Date.now(); return; }
    if (!hiddenAt || !intent() || intent().t > hiddenAt) return;
    // A page restored from the back/forward cache reports visible too; let its pageshow name the return first.
    root.setTimeout(() => { if (dialog.open) return; checkout = false; checkoutCounted = false; paymentUntil = 0; returnTry = 0; returned('tab_return', true); }, 150);
  });
  if (intent() && !purchased() && !result) {
    const go = () => { returnTry = 0; returned('return_pageview', false); };
    if (document.readyState === 'complete') root.setTimeout(go, 800); else root.addEventListener('load', () => root.setTimeout(go, 800), { once: true });
  }
  document.addEventListener('mouseout', event => {
    if (event.relatedTarget || event.clientY > 0 || !fineHover()) return;
    const seen = intent();
    if (!seen || purchased() || Date.now() - seen.t < 1500) return;
    autoShow('exit_intent_after_buy');
  });
  root.PocketEmailOffer = {
    available: () => !excludedPage && (!Number.isFinite(expiresAt) || Date.now() < expiresAt) && !claimed &&
      dialog.dataset.subscribed !== 'true' && dialog.dataset.hasOrdered !== 'true' && !purchased(),
    open: reason => show(reason || 'manual', true)
  };
  schedule();
})(typeof window !== 'undefined' ? window : globalThis);
